import React from "react";
import { Button, DatePicker } from "@pd/pd-ui-kit";
import { Spin } from "antd";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import CheckboxInput from "~/shared/ui/Fields/CheckBoxInput";
import { useGetDealersIvideonSelectLazy } from "~/entity/Ivideon/hooks/useGetDealersIvideonSelectLazy";
import { ActStatus } from "~/entity/Ivideon/model/schema";
import { useURLParamsGetter } from "~/hooks/useURLParamsSetter";
import {
  ACT_STATUSES,
  DEFAULT_ACT_STATUSES,
  SHOW_CHECK_STATUS_TOGGLE,
} from "~/pages/RegisterOfDocuments/options";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { Select } from "~/shared/ui/Select";

import { toServer } from "./helper";

import styles from "./FilterRegisterOfDocuments.less";

export interface useFormIProps {
  dealerSelect: { label: string; value: number }[];
  dateFrom?: Date | null;
  dateTo?: Date | null;
  actStatus?: { label: string; value: ActStatus }[];
}

interface FilterRegisterOfDocumentsIProps {
  getRequestParams: (props: any) => void;
  page?: string;
  count?: string;
}

const FilterRegisterOfDocuments = ({
  getRequestParams,
  page,
  count,
}: FilterRegisterOfDocumentsIProps) => {
  const { t } = useTranslation();
  const { dealerId } = useURLParamsGetter<{
    dealerId: string;
  }>();
  const currentDealerId = useCurrentDealerId();

  const statusOption = (status: ActStatus) => ({
    label: t(`registerOfDocuments.status.${status}`),
    value: status,
  });
  const {
    options,
    isFetching,
    isFetchingNextPage,
    debouncedSearch,
    handleScroll,
    onOpenChange,
  } = useGetDealersIvideonSelectLazy();

  const { control, errors, reset, handleSubmit } = useForm<useFormIProps>({
    defaultValues: {
      dealerSelect: dealerId
        ? [
            {
              label: sessionStorage.getItem("currentDealerName") ?? "",
              value: Number(currentDealerId) ?? 0,
            },
          ]
        : [],
      dateFrom: null,
      dateTo: null,
      actStatus: DEFAULT_ACT_STATUSES.map(statusOption),
    },
  });

  const submit = (data: useFormIProps) => {
    getRequestParams(toServer(data, count, page));
  };

  return (
    <form className={styles.formFilter} onSubmit={handleSubmit(submit)}>
      <div className={styles.filterWrapper}>
        <div className={styles.selectWrapper}>
          <span>Дилер</span>
          <Controller
            name={"dealerSelect"}
            control={control}
            render={({ onChange, value, onBlur }, { invalid }) => (
              <div className={styles.selectWrapp}>
                <Select
                  mode="multiple"
                  labelInValue
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  status={invalid ? "error" : ""}
                  showSearch
                  filterOption={false}
                  onSearch={debouncedSearch}
                  onPopupScroll={handleScroll}
                  options={options}
                  loading={isFetching}
                  onOpenChange={onOpenChange}
                  placeholder="Выберите дилеров..."
                  style={{ width: "100%" }}
                  notFoundContent={
                    isFetching && !isFetchingNextPage ? (
                      <Spin size="small" />
                    ) : (
                      "Нет данных"
                    )
                  }
                />
                {invalid && (
                  <p className={styles.error}>
                    {(errors?.dealerSelect as any)?.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>
        <div className={styles.selectWrapper}>
          <span>Статус акта</span>
          <Controller
            name="actStatus"
            control={control}
            render={({ onChange, value }) => (
              <Select
                mode="multiple"
                labelInValue
                value={value}
                onChange={onChange}
                options={ACT_STATUSES.map(statusOption)}
                placeholder="Выберите статусы..."
                style={{ width: "100%" }}
              />
            )}
          />
        </div>
        <div className={styles.rangeWithButtons}>
          <div className={styles.rangeWrapper}>
            <span>Период формирования акта</span>
            <div className={styles.rangeWrapp}>
              <Controller
                control={control}
                name="dateFrom"
                render={({ onChange, value, onBlur }) => (
                  <DatePicker
                    placeholder="от дд.мм.гггг"
                    format="DD.MM.YYYY"
                    value={value}
                    onBlur={onBlur}
                    onChange={(date: Date) => onChange(date)}
                  />
                )}
              />
              <span>-</span>
              <Controller
                control={control}
                name="dateTo"
                render={({ onChange, value, onBlur }) => (
                  <DatePicker
                    placeholder="до дд.мм.гггг"
                    format="DD.MM.YYYY"
                    value={value}
                    onBlur={onBlur}
                    onChange={(date: Date) => onChange(date)}
                  />
                )}
              />
            </div>
          </div>
          <div className={styles.buttons}>
            <Button
              htmlType="button"
              color="secondary"
              variant="text"
              bold={false}
              fontSize="12"
              size="middle"
              onClick={() => reset()}
            >
              Сбросить
            </Button>
            <Button
              htmlType="submit"
              bold
              color="secondary"
              fontSize="12"
              size="middle"
            >
              Показать
            </Button>
          </div>
        </div>
      </div>
      {SHOW_CHECK_STATUS_TOGGLE && (
        <div className={styles.checkboxStyle}>
          <CheckboxInput
            labelClass={styles.reverseLabel}
            label="Наличие актов/счетов"
            name="checkStatus"
            control={control}
            defaultValue={false}
          />
        </div>
      )}
    </form>
  );
};

export default FilterRegisterOfDocuments;
